import { PlayerTagVisual } from '@cardforest/renderer/player-tag';
import type { ChipLegendEntry } from '@everyturn/protocol';
import { Alert, AlertDescription, AlertTitle, Button } from '@workspace/app-components';
import type { MDXComponents, MDXProps } from 'mdx/types';
import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { TableSurface } from './table-surface';

export { TableSurface } from './table-surface';

export function ChipLegendSection({ chipLegend }: { chipLegend: readonly ChipLegendEntry[] }) {
  return (
    <section aria-label="Player Status Indicators" data-document-example className="my-6 flex min-w-0 flex-col gap-4">
      <h4 className="font-semibold">Player Status Indicators</h4>
      <p className="text-sm text-muted-foreground">Select an example player tag to inspect its indicators.</p>
      <dl className="flex flex-col gap-4">
        {chipLegend.map((entry) => (
          <div key={entry.title} className="flex min-w-0 flex-col gap-3">
            <dt className="text-sm font-medium">{entry.title}</dt>
            <dd className="grid min-w-0 items-center gap-4 sm:grid-cols-2">
              <TableSurface role="group" aria-label={`${entry.title} example: ${entry.label}`} className="p-3">
                <div className="flex min-h-44 items-end justify-center pb-2">
                  <PlayerTagVisual
                    name="Alex"
                    mode="idle"
                    seatLetter="S"
                    seatName="South"
                    seatColor="blue"
                    fontSize={18}
                    playerId="indicator-example"
                    chips={[
                      {
                        label: entry.label,
                        color: entry.color,
                        wide: entry.wide,
                        description: entry.title,
                      },
                    ]}
                  />
                </div>
              </TableSurface>
              <p className="text-sm leading-relaxed text-muted-foreground">{entry.description}</p>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function Callout({
  title,
  type = 'info',
  children,
}: {
  title?: string;
  type?: 'info' | 'warning' | 'error';
  children: ReactNode;
}) {
  return (
    <Alert role="note" variant={type === 'error' ? 'destructive' : 'default'} className="my-5">
      <AlertTitle>{title ?? { info: 'Note', warning: 'Warning', error: 'Important' }[type]}</AlertTitle>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export class DocumentBoundary extends Component<
  { id: string; children: ReactNode; document?: boolean },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[Document ${this.props.id}] Rendering failed:`, error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Alert variant="destructive">
        <AlertTitle>{this.props.document ? 'Document unavailable' : 'Example unavailable'}</AlertTitle>
        <AlertDescription>
          <p>
            {this.props.document
              ? 'The document could not render. Please try again.'
              : 'The interactive example failed. Its explanation remains available above.'}
          </p>
          <Button variant="outline" className="min-h-11" onClick={() => this.setState({ failed: false })}>
            {this.props.document ? 'Try document again' : 'Try example again'}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
}

const documentComponents = { Callout, Example: DocumentBoundary } satisfies MDXComponents;

export function DocumentContent({
  Content,
  components,
}: {
  Content: ComponentType<MDXProps>;
  components?: MDXComponents;
}) {
  return (
    <DocumentBoundary id="document" document>
      <Content components={{ ...documentComponents, ...components }} />
    </DocumentBoundary>
  );
}
